/**
 * Voice Memos Test Prototype
 * Drag-and-drop voice memo management UI with secondary panel support
 *
 * Access: http://flownotes.localhost/?test=voice-memos
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Mic, Upload, ArrowLeft, ChevronDown, LayoutGrid, LayoutList, List, Maximize2, Package, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { SecondaryPanel } from '@/components/layout/secondary-panel';
import { PANEL_SIZES } from '@/lib/panel-sizes';

// Types
import type {
  VoiceMemo,
  VoiceMemoPackage,
  VoiceMemoGroup,
  StatusFilter,
  SortType,
  ViewMode,
  PanelContent,
  ThemeFilter
} from './types';

// Mock data
import { generateMockData } from './mock-data';

// Utilities
import { groupMemosBySession, generatePackages } from './utils';

// Components
import { StatusBadge } from './voice-memo-status-badge';
import { VoiceMemoCard } from './voice-memo-card';
import { VoiceMemoListItem } from './voice-memo-list-item';
import { VoiceMemoDropZone } from './voice-memo-drop-zone';
import { VoiceMemoSessionGroup } from './voice-memo-session-group';
import { VoiceMemoTranscriptPanel } from './voice-memo-transcript-panel';
import { VoiceMemoAnalysisPanel } from './voice-memo-analysis-panel';
import { VoiceMemoPackagePanel } from './voice-memo-package-panel';
import { VoiceMemoSidebar } from './voice-memo-sidebar';
import { VoiceMemoDetailView } from './voice-memo-detail-view';
import { VoiceMemoDocumentList } from './voice-memo-document-list';
import { VoiceMemoBreadcrumb } from './voice-memo-breadcrumb';
import { VoiceMemoSettingsPanel } from './voice-memo-settings-panel';
import { VoiceMemoPackageHeader } from './voice-memo-package-header';

export function TestVoiceMemos() {
  const [voiceMemos, setVoiceMemos] = useState<VoiceMemo[]>(generateMockData());
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [themeFilter, setThemeFilter] = useState<ThemeFilter>('all');
  const [sortBy, setSortBy] = useState<SortType>('recent');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('package'); // Default to package view
  const [isUploading, setIsUploading] = useState(false);

  // Panel state
  const [panelContent, setPanelContent] = useState<PanelContent>(null);
  const [selectedMemo, setSelectedMemo] = useState<VoiceMemo | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<VoiceMemoPackage | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [selectedMemoId, setSelectedMemoId] = useState<string | null>(null);

  // Preview panel state - for associated documents
  const [previewDocuments, setPreviewDocuments] = useState<Array<{id: string, title: string, type: 'transcript' | 'analysis', memoId: string, memo: VoiceMemo}>>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);

  // Sidebar state - show package detail view when package is selected
  const [showPackageDetail, setShowPackageDetail] = useState(false);

  // Detail view state - show single memo detail (Basecamp-like)
  const [showMemoDetail, setShowMemoDetail] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    autoTranscribe: false,
    autoAnalyze: false,
    autoTranscribeOnUpload: false,
    autoAnalyzeOnUpload: false,
    autoTranscribeOnRecord: false,
    autoAnalyzeOnRecord: false
  });

  // Package name overrides (for renamed packages)
  const [packageNameOverrides, setPackageNameOverrides] = useState<Record<string, string>>({});

  // Transcribing state
  const [transcribingIds, setTranscribingIds] = useState<Set<string>>(new Set());

  // Generate packages from memos
  const packages = useMemo(() => {
    const generated = generatePackages(voiceMemos);
    // Apply name overrides
    return generated.map(pkg => ({
      ...pkg,
      packageName: packageNameOverrides[pkg.id] || pkg.packageName
    }));
  }, [voiceMemos, packageNameOverrides]);

  // Get all unique themes dynamically
  const availableThemes = useMemo(() => {
    const themeSet = new Set<string>();
    voiceMemos.forEach(memo => {
      if (memo.theme) {
        themeSet.add(memo.theme);
      }
    });
    return Array.from(themeSet).sort();
  }, [voiceMemos]);

  // Sort and filter memos/packages
  const filteredPackages = useMemo((): VoiceMemoPackage[] => {
    let filtered: VoiceMemoPackage[] = [...packages];

    // Apply theme filter
    if (themeFilter !== 'all') {
      filtered = filtered.map(pkg => {
        const themeItemIds = pkg.packageMetadata.themes?.[themeFilter] || [];
        if (themeItemIds.length === 0) return null;
        return {
          ...pkg,
          items: pkg.items.filter(item => themeItemIds.includes(item.id))
        };
      }).filter((pkg) => pkg !== null) as VoiceMemoPackage[];
    }

    // Apply status filter to packages (filter items within packages)
    if (statusFilter !== 'all') {
      filtered = filtered.map(pkg => ({
        ...pkg,
        items: pkg.items.filter(m => m.status === statusFilter)
      })).filter(pkg => pkg.items.length > 0);
    }

    return filtered;
  }, [packages, themeFilter, statusFilter]);

  // Sort and filter memos, then group (for non-package views)
  const groupedMemos = useMemo(() => {
    let filtered = [...voiceMemos];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(m => m.status === statusFilter);
    }

    // Apply theme filter
    if (themeFilter !== 'all') {
      filtered = filtered.filter(m => m.theme === themeFilter);
    }

    // Apply sorting
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => {
          const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
          if (dateCompare !== 0) return dateCompare;
          const timeA = a.timestamp || '000000';
          const timeB = b.timestamp || '000000';
          return timeB.localeCompare(timeA);
        });
        break;
      case 'oldest':
        filtered.sort((a, b) => {
          const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
          if (dateCompare !== 0) return dateCompare;
          const timeA = a.timestamp || '000000';
          const timeB = b.timestamp || '000000';
          return timeA.localeCompare(timeB);
        });
        break;
      case 'title-asc':
        filtered.sort((a, b) => a.filename.localeCompare(b.filename));
        break;
      case 'title-desc':
        filtered.sort((a, b) => b.filename.localeCompare(a.filename));
        break;
    }

    // Group memos by session (only in list view)
    if (viewMode === 'list') {
      return groupMemosBySession(filtered);
    }

    return filtered;
  }, [voiceMemos, statusFilter, themeFilter, sortBy, viewMode]);

  const counts = useMemo(() => ({
    all: voiceMemos.length,
    unprocessed: voiceMemos.filter(m => m.status === 'unprocessed').length,
    transcribed: voiceMemos.filter(m => m.status === 'transcribed').length,
    analyzed: voiceMemos.filter(m => m.status === 'analyzed').length,
    filed: voiceMemos.filter(m => m.status === 'filed').length,
    ignored: voiceMemos.filter(m => m.status === 'ignored').length
  }), [voiceMemos]);

  const handleFilesDropped = useCallback((files: File[]) => {
    setIsUploading(true);

    // Simulate upload delay
    setTimeout(() => {
      const newMemos: VoiceMemo[] = files.map(file => ({
        id: `vm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        filename: file.name,
        duration: '0:00',
        date: new Date().toISOString().split('T')[0],
        status: 'unprocessed',
        fileSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`
      }));

      setVoiceMemos(prev => [...newMemos, ...prev]);
      setIsUploading(false);
    }, 1000);
  }, []);

  const handleProcess = useCallback((id: string) => {
    // Set transcribing state
    setTranscribingIds(prev => new Set(prev).add(id));

    // Update status to transcribing
    setVoiceMemos(prev => prev.map(memo =>
      memo.id === id ? { ...memo, status: 'transcribing' as const } : memo
    ));

    // Simulate transcription process (3-5 seconds)
    const transcriptionDelay = 3000 + Math.random() * 2000;

    setTimeout(() => {
      setVoiceMemos(prev => prev.map(memo => {
        if (memo.id === id) {
          return {
            ...memo,
            status: 'transcribed' as const,
            transcript: {
              path: `inbox/voice-memos/${memo.filename.replace(/\.(m4a|wav|m4b)$/, '.md')}`,
              content: `# ${memo.filename.replace(/\.(m4a|wav|m4b)$/, '')}\n\n## Raw Transcript\n\n[Transcribed content would appear here]`
            }
          };
        }
        return memo;
      }));

      // Remove from transcribing set
      setTranscribingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, transcriptionDelay);
  }, []);

  const handleViewTranscript = useCallback((memo: VoiceMemo) => {
    if (!memo.transcript?.path) {
      console.warn('No transcript path available');
      return;
    }
    setSelectedMemo(memo);
    setSelectedMemoId(memo.id);
    setPanelContent('transcript');
  }, []);

  const handleReviewAndFile = useCallback((memo: VoiceMemo) => {
    if (!memo.transcript?.path) {
      console.warn('No transcript path available');
      return;
    }
    setSelectedMemo(memo);
    setSelectedMemoId(memo.id);
    setPanelContent('analysis');
  }, []);

  const handleViewPackage = useCallback((pkg: VoiceMemoPackage) => {
    setSelectedPackage(pkg);
    setSelectedPackageId(pkg.id);
    setShowPackageDetail(true);
    setShowMemoDetail(false);
    setPanelContent(null); // Don't open package in right panel - it's in main panel
    setPreviewDocuments([]);
  }, []);

  const handleSelectPackageFromSidebar = useCallback((pkg: VoiceMemoPackage) => {
    setSelectedPackage(pkg);
    setSelectedPackageId(pkg.id);
    setShowPackageDetail(true);
    setShowMemoDetail(false);
    setViewMode('package');
    setPanelContent(null);
    setPreviewDocuments([]);
  }, []);

  const handleViewMemoFromPackage = useCallback((memo: VoiceMemo) => {
    setSelectedMemo(memo);
    setSelectedMemoId(memo.id);
    setShowMemoDetail(true);
    setShowPackageDetail(false);
    setPanelContent(null); // Don't auto-open - let user click sections
    setPreviewDocuments([]);
  }, []);

  const handleViewDocuments = useCallback((type: 'transcript' | 'analysis', memos: VoiceMemo[]) => {
    const documents = memos
      .filter(m => {
        if (type === 'transcript') return m.transcript?.path;
        if (type === 'analysis') return m.status === 'analyzed' || m.summary;
        return false;
      })
      .map(m => ({
        id: m.id,
        title: m.title || m.filename.replace(/\.(m4a|wav|m4b)$/, ''),
        type,
        memoId: m.id,
        memo: m
      }));

    if (documents.length === 0) return;

    setPreviewDocuments(documents);
    setPanelContent(type === 'transcript' ? 'transcript' : 'analysis');

    // If only one document, open it immediately
    if (documents.length === 1) {
      setSelectedMemo(documents[0].memo);
      setSelectedMemoId(documents[0].memoId);
      setSelectedDocumentId(documents[0].id);
    } else {
      // Multiple documents - show list, user can click to view
      setSelectedDocumentId(null);
      setSelectedMemo(null);
    }
  }, []);

  const handleSelectDocument = useCallback((document: {id: string, memo: VoiceMemo, type: 'transcript' | 'analysis'}) => {
    setSelectedMemo(document.memo);
    setSelectedMemoId(document.memo.id);
    setSelectedDocumentId(document.id);
    setPanelContent(document.type === 'transcript' ? 'transcript' : 'analysis');
  }, []);

  const handleViewMemoDetail = useCallback((memo: VoiceMemo) => {
    setSelectedMemo(memo);
    setSelectedMemoId(memo.id);
    setShowMemoDetail(true);
    setShowPackageDetail(false);
  }, []);

  const handleBackFromDetail = useCallback(() => {
    setShowMemoDetail(false);
    setSelectedMemo(null);
    setSelectedMemoId(null);
  }, []);

  const handleAnalyze = useCallback((memo: VoiceMemo) => {
    // Open AI chat in preview panel
    setSelectedMemo(memo);
    setSelectedMemoId(memo.id);
    setPanelContent('analysis');
    // TODO: Trigger AI analysis with chat interface
    console.log('Analyze voice memo:', memo.id);
  }, []);

  const handleProcessAll = useCallback((ids: string[]) => {
    // Process all memos: transcribe first, then analyze
    ids.forEach((id, index) => {
      // Stagger transcription
      setTimeout(() => {
        handleProcess(id);
      }, index * 500);

      // After transcription completes, queue analysis
      // (In real implementation, this would wait for transcription to complete)
      setTimeout(() => {
        setVoiceMemos(prev => prev.map(memo => {
          if (memo.id === id && memo.status === 'transcribed') {
            // Queue analysis
            setTimeout(() => {
              setVoiceMemos(prevMemos => prevMemos.map(m => {
                if (m.id === id && m.status === 'transcribed') {
                  return {
                    ...m,
                    status: 'analyzed' as const,
                    summary: `Analysis for ${m.title || m.filename}`,
                    actionItems: ['Action item 1', 'Action item 2']
                  };
                }
                return m;
              }));
            }, 2000); // Simulate analysis delay
          }
          return memo;
        }));
      }, (index * 500) + 5000); // After transcription delay
    });
  }, [handleProcess]);

  const handleUpdatePackageName = useCallback((packageId: string, newName: string) => {
    setPackageNameOverrides(prev => ({
      ...prev,
      [packageId]: newName
    }));
  }, []);

  const handleGenerateTitle = useCallback(async (memoId: string) => {
    const memo = voiceMemos.find(m => m.id === memoId);
    if (!memo?.transcript?.content) return;

    // Extract raw transcript for title generation
    const rawTranscriptMatch = memo.transcript.content.match(/## Raw Transcript\s*\n([\s\S]*?)(?=\n##|$)/);
    const rawTranscript = rawTranscriptMatch ? rawTranscriptMatch[1].trim() : memo.transcript.content;

    // Simulate AI title generation (in real implementation, this would call API)
    // Use first meaningful sentence or key phrase
    const sentences = rawTranscript.split(/[.!?]+/).filter(s => s.trim().length > 15);
    let generatedTitle = '';

    if (sentences.length > 0) {
      const firstSentence = sentences[0].trim();
      // Clean up common fillers
      generatedTitle = firstSentence
        .replace(/^(um|uh|so|well|okay|alright|hey|hi|hello|i'm|i've|i had|i was)\s+/i, '')
        .substring(0, 60)
        .trim();

      // Capitalize first letter
      if (generatedTitle) {
        generatedTitle = generatedTitle.charAt(0).toUpperCase() + generatedTitle.slice(1);
      }
    }

    if (!generatedTitle) {
      // Fallback: use filename without extension
      generatedTitle = memo.filename.replace(/\.(m4a|wav|m4b)$/i, '').replace(/[-_]/g, ' ');
    }

    // Update memo with generated title
    setVoiceMemos(prev => prev.map(m =>
      m.id === memoId ? { ...m, title: generatedTitle } : m
    ));
  }, [voiceMemos]);

  const handleUpdateTitle = useCallback((memoId: string, title: string) => {
    setVoiceMemos(prev => prev.map(m =>
      m.id === memoId ? { ...m, title } : m
    ));
  }, []);

  const handleClosePanel = useCallback(() => {
    setPanelContent(null);
    setSelectedMemo(null);
    setSelectedPackage(null);
    setShowPackageDetail(false);
    setSelectedPackageId(null);
  }, []);

  const getStatusLabel = (status: StatusFilter) => {
    switch (status) {
      case 'unprocessed': return 'Unprocessed';
      case 'transcribed': return 'Transcribed';
      case 'analyzed': return 'Analyzed';
      case 'filed': return 'Filed';
      case 'ignored': return 'Ignored';
      default: return 'All Statuses';
    }
  };

  const getSortLabel = (sort: SortType) => {
    switch (sort) {
      case 'recent': return 'Most Recent';
      case 'oldest': return 'Oldest First';
      case 'title-asc': return 'Title (A-Z)';
      case 'title-desc': return 'Title (Z-A)';
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b p-4 bg-sidebar flex-shrink-0">
        <div className="flex items-center gap-3 mb-2">
          <a
            href="/?test=index"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Prototype Index</span>
          </a>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <Mic className="w-6 h-6" />
          <h1 className="text-xl font-bold">Voice Memos Test Prototype</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Drag-and-drop voice memo management with secondary panel support
        </p>
      </div>

      {/* Split Panel Layout */}
      <PanelGroup direction="horizontal" className="flex-1 overflow-hidden">
        {/* LEFT SIDEBAR - Package Navigation */}
        <Panel
          defaultSize={20}
          minSize={15}
          maxSize={30}
          className="overflow-hidden flex flex-col"
        >
          <VoiceMemoSidebar
            packages={filteredPackages}
            selectedPackageId={selectedPackageId}
            onSelectPackage={handleSelectPackageFromSidebar}
          />
        </Panel>

        <PanelResizeHandle className="w-1 bg-border hover:bg-primary/20 transition-colors" />

        {/* MAIN PANEL - Voice Memo List or Package Detail */}
        <Panel
          defaultSize={panelContent ? 50 : 80}
          minSize={panelContent ? 30 : 50}
          maxSize={panelContent ? undefined : 100}
          className="overflow-hidden flex flex-col"
        >
          {/* Header with Breadcrumb */}
          <div className="px-6 py-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              {/* Breadcrumb Navigation */}
              <div className="flex-1 min-w-0">
                {showMemoDetail && selectedMemo ? (
                  <VoiceMemoBreadcrumb
                    items={[
                      {
                        label: 'Voice Memos',
                        onClick: () => {
                          setShowMemoDetail(false);
                          setShowPackageDetail(false);
                          setSelectedMemo(null);
                          setSelectedMemoId(null);
                          setSelectedPackage(null);
                          setSelectedPackageId(null);
                        }
                      },
                      selectedPackage ? {
                        label: selectedPackage.packageName || `${selectedPackage.packageDate} Voice Memos`,
                        onClick: () => {
                          setShowMemoDetail(false);
                          setShowPackageDetail(true);
                          setSelectedMemo(null);
                          setSelectedMemoId(null);
                        }
                      } : null,
                      {
                        label: selectedMemo.title || selectedMemo.filename.replace(/\.(m4a|wav|m4b)$/, '')
                      }
                    ].filter(Boolean) as Array<{label: string, onClick?: () => void}>}
                  />
                ) : showPackageDetail && selectedPackage ? (
                  <VoiceMemoBreadcrumb
                    items={[
                      {
                        label: 'Voice Memos',
                        onClick: () => {
                          setShowPackageDetail(false);
                          setSelectedPackage(null);
                          setSelectedPackageId(null);
                        }
                      },
                      {
                        label: selectedPackage.packageName || `${selectedPackage.packageDate} Voice Memos`
                      }
                    ]}
                  />
                ) : (
                  <VoiceMemoBreadcrumb
                    items={[
                      {
                        label: 'Voice Memos'
                      }
                    ]}
                  />
                )}
              </div>

              <div className="flex items-center gap-2">
                <VoiceMemoSettingsPanel
                  settings={settings}
                  onSettingsChange={setSettings}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Focus view (hide sidebars)"
                  title="Focus view (hide sidebars)"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  className="gap-2"
                  onClick={() => document.getElementById('voice-memo-upload-input')?.click()}
                  aria-label="Upload voice memos"
                >
                  <Upload className="h-4 w-4" />
                  Upload
                </Button>
              </div>
            </div>

            {/* Filters - Only show in package/list views, not detail view */}
            {!showMemoDetail && (
            <div className="flex items-center gap-2">
              {/* Status Filter */}
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => setShowStatusMenu(!showStatusMenu)}
                  aria-label={`Filter by status: ${getStatusLabel(statusFilter)}`}
                  aria-expanded={showStatusMenu}
                >
                  {getStatusLabel(statusFilter)} ({counts[statusFilter] || 0})
                  <ChevronDown className="h-3 w-3" aria-hidden="true" />
                </Button>
                {showStatusMenu && (
                  <div className="absolute top-full mt-1 left-0 bg-background border border-border rounded-md shadow-lg z-10 min-w-[160px]">
                    {(['all', 'unprocessed', 'transcribed', 'analyzed', 'filed', 'ignored'] as StatusFilter[]).map((status) => (
                      <button
                        key={status}
                        onClick={() => {
                          setStatusFilter(status);
                          setShowStatusMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                          statusFilter === status ? "bg-muted font-medium" : ""
                        }`}
                      >
                        {getStatusLabel(status)} ({counts[status]})
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Theme Filter */}
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => setShowThemeMenu(!showThemeMenu)}
                  aria-label={`Filter by theme: ${themeFilter === 'all' ? 'All themes' : themeFilter}`}
                  aria-expanded={showThemeMenu}
                >
                  {themeFilter === 'all' ? 'All Themes' : themeFilter}
                  <ChevronDown className="h-3 w-3" aria-hidden="true" />
                </Button>
                {showThemeMenu && (
                  <div className="absolute top-full mt-1 left-0 bg-background border border-border rounded-md shadow-lg z-10 min-w-[160px]">
                    <button
                      onClick={() => {
                        setThemeFilter('all');
                        setShowThemeMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                        themeFilter === 'all' ? "bg-muted font-medium" : ""
                      }`}
                    >
                      All Themes
                    </button>
                    {availableThemes.map((theme) => (
                      <button
                        key={theme}
                        onClick={() => {
                          setThemeFilter(theme);
                          setShowThemeMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                          themeFilter === theme ? "bg-muted font-medium" : ""
                        }`}
                      >
                        {theme}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort */}
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => setShowSortMenu(!showSortMenu)}
                  aria-label={`Sort by: ${getSortLabel(sortBy)}`}
                  aria-expanded={showSortMenu}
                >
                  {getSortLabel(sortBy)}
                  <ChevronDown className="h-3 w-3" aria-hidden="true" />
                </Button>
                {showSortMenu && (
                  <div className="absolute top-full mt-1 left-0 bg-background border border-border rounded-md shadow-lg z-10 min-w-[160px]">
                    {(['recent', 'oldest', 'title-asc', 'title-desc'] as SortType[]).map((sort) => (
                      <button
                        key={sort}
                        onClick={() => {
                          setSortBy(sort);
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                          sortBy === sort ? "bg-muted font-medium" : ""
                        }`}
                      >
                        {getSortLabel(sort)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex items-center border border-border rounded-md p-0.5 ml-auto" role="group" aria-label="View mode">
                <button
                  onClick={() => setViewMode('card')}
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === 'card'
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted text-muted-foreground'
                  }`}
                  title="Card view"
                  aria-pressed={viewMode === 'card'}
                >
                  <LayoutList className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted text-muted-foreground'
                  }`}
                  title="Grid view"
                  aria-pressed={viewMode === 'grid'}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === 'list'
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted text-muted-foreground'
                  }`}
                  title="List view"
                  aria-pressed={viewMode === 'list'}
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('package')}
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === 'package'
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted text-muted-foreground'
                  }`}
                  title="Package view (groups)"
                  aria-pressed={viewMode === 'package'}
                >
                  <Package className="h-4 w-4" />
                </button>
              </div>
            </div>
            )}
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto">
            {/* Single Memo Detail View (Basecamp-like) */}
            {showMemoDetail && selectedMemo && (
              <VoiceMemoDetailView
                memo={selectedMemo}
                package={(selectedMemo.packageId ? packages.find(p => p.id === selectedMemo.packageId) : selectedPackage) ?? undefined}
                onViewTranscript={() => {
                  // Single memo - open transcript directly
                  setSelectedMemo(selectedMemo);
                  setSelectedMemoId(selectedMemo.id);
                  setPanelContent('transcript');
                  setPreviewDocuments([]);
                }}
                onViewAnalysis={() => {
                  // Single memo - open analysis directly
                  setSelectedMemo(selectedMemo);
                  setSelectedMemoId(selectedMemo.id);
                  setPanelContent('analysis');
                  setPreviewDocuments([]);
                }}
                onAnalyze={() => handleAnalyze(selectedMemo)}
                onBack={handleBackFromDetail}
              />
            )}

            {/* Package Detail View (when selected from sidebar) */}
            {!showMemoDetail && showPackageDetail && selectedPackage && (
              <div className="p-6 space-y-6">
                <div className="border-b border-border pb-4">
                  <VoiceMemoPackageHeader
                    package={selectedPackage}
                    onUpdateName={handleUpdatePackageName}
                  />
                </div>

                {/* Unified Analysis */}
                {selectedPackage.packageMetadata.unifiedAnalysis && (
                  <div className="border-b border-border pb-4">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      Unified Analysis
                    </h3>
                    {selectedPackage.packageMetadata.unifiedAnalysis.summary && (
                      <p className="text-sm text-foreground mb-3">{selectedPackage.packageMetadata.unifiedAnalysis.summary}</p>
                    )}
                    {selectedPackage.packageMetadata.unifiedAnalysis.actionItems && selectedPackage.packageMetadata.unifiedAnalysis.actionItems.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2">Action Items</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                          {selectedPackage.packageMetadata.unifiedAnalysis.actionItems.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedPackage.packageMetadata.unifiedAnalysis.themes && selectedPackage.packageMetadata.unifiedAnalysis.themes.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {selectedPackage.packageMetadata.unifiedAnalysis.themes.map((theme, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {theme}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Theme Sections */}
                {Object.keys(selectedPackage.packageMetadata.themes || {}).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      Themes ({Object.keys(selectedPackage.packageMetadata.themes || {}).length})
                    </h3>
                    <div className="space-y-4">
                      {Object.keys(selectedPackage.packageMetadata.themes || {}).map(theme => {
                        const themeItemIds = selectedPackage.packageMetadata.themes?.[theme] || [];
                        const themeItems = selectedPackage.items.filter(item => themeItemIds.includes(item.id));

                        return (
                          <div key={theme} className="border border-border rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant="secondary">{theme}</Badge>
                              <span className="text-xs text-muted-foreground">{themeItems.length} memos</span>
                            </div>
                            <div className="space-y-1">
                              {themeItems.map(item => (
                                <button
                                  key={item.id}
                                  onClick={() => handleViewMemoFromPackage(item)}
                                  className="w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-muted/50"
                                >
                                  {item.title || item.filename}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* All Memos List */}
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    All Memos ({selectedPackage.items.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedPackage.items.map(memo => (
                      <button
                        key={memo.id}
                        onClick={() => handleViewMemoFromPackage(memo)}
                        className="w-full text-left p-2 border border-border rounded hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{memo.title || memo.filename}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                              <span>{memo.duration}</span>
                              {memo.theme && (
                                <>
                                  <span>•</span>
                                  <Badge variant="outline" className="text-xs">{memo.theme}</Badge>
                                </>
                              )}
                            </div>
                          </div>
                          <StatusBadge status={memo.status} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Associated Documents Section (like Work Specs in Basecamp) */}
                <div className="border-t border-border pt-6">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Documents
                  </h3>

                  {/* Transcripts Section */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-muted-foreground">
                        TRANSCRIPTS {selectedPackage.items.filter(m => m.transcript?.path).length}
                      </h4>
                      {selectedPackage.items.filter(m => m.transcript?.path).length > 0 && (
                        <button
                          onClick={() => handleViewDocuments('transcript', selectedPackage.items)}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          View all transcripts
                        </button>
                      )}
                    </div>
                    {selectedPackage.items.filter(m => m.transcript?.path).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No transcripts found</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {selectedPackage.items.filter(m => m.transcript?.path).length} transcript{selectedPackage.items.filter(m => m.transcript?.path).length !== 1 ? 's' : ''} available
                      </p>
                    )}
                  </div>

                  {/* Analysis Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-muted-foreground">
                        ANALYSIS {selectedPackage.items.filter(m => m.status === 'analyzed' || m.summary).length}
                      </h4>
                      {selectedPackage.items.filter(m => m.status === 'analyzed' || m.summary).length > 0 && (
                        <button
                          onClick={() => handleViewDocuments('analysis', selectedPackage.items)}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          View all analysis
                        </button>
                      )}
                    </div>
                    {selectedPackage.items.filter(m => m.status === 'analyzed' || m.summary).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No analysis found</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {selectedPackage.items.filter(m => m.status === 'analyzed' || m.summary).length} analysis document{selectedPackage.items.filter(m => m.status === 'analyzed' || m.summary).length !== 1 ? 's' : ''} available
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Package View (grid of packages) */}
            {!showMemoDetail && !showPackageDetail && viewMode === 'package' && (
              <div className="p-6">
                {filteredPackages.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground mb-4">No packages match your filters</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {filteredPackages.map(pkg => (
                      <Card key={pkg.id} className="p-4 hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => handleViewPackage(pkg)}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-semibold">{pkg.packageName || `${pkg.packageDate} Voice Memos`}</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {pkg.items.length} memos • {pkg.packageDate} • {pkg.groupingType}
                            </p>
                          </div>
                          <StatusBadge status={pkg.status === 'analyzed' ? 'analyzed' : 'transcribed'} />
                        </div>
                        {pkg.packageMetadata.unifiedAnalysis?.summary && (
                          <p className="text-sm text-foreground mb-3 line-clamp-2">
                            {pkg.packageMetadata.unifiedAnalysis.summary}
                          </p>
                        )}
                        {pkg.packageMetadata.themes && Object.keys(pkg.packageMetadata.themes).length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {Object.keys(pkg.packageMetadata.themes).map(theme => (
                              <Badge key={theme} variant="secondary" className="text-xs">
                                {theme} ({pkg.packageMetadata.themes![theme].length})
                              </Badge>
                            ))}
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Other Views */}
            {!showMemoDetail && !showPackageDetail && viewMode !== 'package' && groupedMemos.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No voice memos match your filters</p>
                <div className="max-w-md mx-auto">
                  <VoiceMemoDropZone
                    onFilesDropped={handleFilesDropped}
                    isUploading={isUploading}
                  />
                </div>
              </div>
            ) : (
              <>
                {/* Card View */}
                {viewMode === 'card' && (
                  <div className="p-6">
                    <div className="space-y-8">
                      {groupedMemos.map((item) => {
                        if ('memos' in item) {
                          // It's a group - show session header and all memos
                          return (
                            <div key={item.id}>
                              <div className="flex items-center gap-3 mb-4 px-2">
                                <div className="h-px flex-1 bg-border" />
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                  Session • {item.date} • {item.startTime.substring(0, 2)}:{item.startTime.substring(2, 4)} - {item.endTime.substring(0, 2)}:{item.endTime.substring(2, 4)} • {item.memos.length} memos
                                </div>
                                <div className="h-px flex-1 bg-border" />
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                {item.memos.map((memo) => (
                                  <VoiceMemoCard
                                    key={memo.id}
                                    memo={memo}
                                    onProcess={handleProcess}
                                    onViewTranscript={handleViewTranscript}
                                    onReviewAndFile={handleReviewAndFile}
                                    isTranscribing={transcribingIds.has(memo.id)}
                                    viewMode="card"
                                    isSelected={selectedMemoId === memo.id}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        } else {
                          // It's a single memo - click to show detail view
                          return (
                            <VoiceMemoCard
                              key={item.id}
                              memo={item}
                              onProcess={handleProcess}
                              onViewTranscript={() => handleViewMemoDetail(item)}
                              onReviewAndFile={() => handleViewMemoDetail(item)}
                              isTranscribing={transcribingIds.has(item.id)}
                              viewMode="card"
                              isSelected={selectedMemoId === item.id}
                            />
                          );
                        }
                      })}
                    </div>
                  </div>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && (
                  <div className="p-6">
                    <div className="space-y-8">
                      {groupedMemos.map((item) => {
                        if ('memos' in item) {
                          // It's a group - show session header and all memos
                          return (
                            <div key={item.id}>
                              <div className="flex items-center gap-3 mb-4 px-2">
                                <div className="h-px flex-1 bg-border" />
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                  Session • {item.date} • {item.startTime.substring(0, 2)}:{item.startTime.substring(2, 4)} - {item.endTime.substring(0, 2)}:{item.endTime.substring(2, 4)} • {item.memos.length} memos
                                </div>
                                <div className="h-px flex-1 bg-border" />
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {item.memos.map((memo) => (
                                  <VoiceMemoCard
                                    key={memo.id}
                                    memo={memo}
                                    onProcess={handleProcess}
                                    onViewTranscript={handleViewTranscript}
                                    onReviewAndFile={handleReviewAndFile}
                                    isTranscribing={transcribingIds.has(memo.id)}
                                    viewMode="grid"
                                    isSelected={selectedMemoId === memo.id}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        } else {
                          // It's a single memo - click to show detail view
                          return (
                            <VoiceMemoCard
                              key={item.id}
                              memo={item}
                              onProcess={handleProcess}
                              onViewTranscript={() => handleViewMemoDetail(item)}
                              onReviewAndFile={() => handleViewMemoDetail(item)}
                              isTranscribing={transcribingIds.has(item.id)}
                              viewMode="grid"
                              isSelected={selectedMemoId === item.id}
                            />
                          );
                        }
                      })}
                    </div>
                  </div>
                )}

                {/* List View - Shows groupings */}
                {viewMode === 'list' && (
                  <div className="p-2">
                    {groupedMemos.map((item) => {
                      if ('memos' in item) {
                        // It's a group
                        return (
                          <VoiceMemoSessionGroup
                            key={item.id}
                            group={item}
                            onProcess={handleProcess}
                            onProcessAll={handleProcessAll}
                            onViewTranscript={handleViewTranscript}
                            onReviewAndFile={handleReviewAndFile}
                            transcribingIds={transcribingIds}
                            selectedMemoId={selectedMemoId}
                          />
                        );
                      } else {
                        // It's a single memo - click to show detail view
                        return (
                          <VoiceMemoListItem
                            key={item.id}
                            memo={item}
                            onProcess={handleProcess}
                            onViewTranscript={() => handleViewMemoDetail(item)}
                            onReviewAndFile={() => handleViewMemoDetail(item)}
                            isTranscribing={transcribingIds.has(item.id)}
                            isSelected={selectedMemoId === item.id}
                          />
                        );
                      }
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Hidden file input for upload button */}
          <input
            type="file"
            id="voice-memo-upload-input"
            multiple
            accept=".m4a,.m4b,.wav,.mp3,audio/*"
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleFilesDropped(Array.from(e.target.files));
                e.target.value = '';
              }
            }}
          />
        </Panel>

        {/* Resizable Handle - Only show when panel is open */}
        {panelContent && (
          <>
            <PanelResizeHandle className="w-1 bg-primary/20 hover:bg-primary/30 transition-colors" />

            {/* RIGHT PANEL - Document List or Single Document Preview */}
            <SecondaryPanel
              panelType={panelContent === 'transcript' ? 'voice-memo-transcript' : 'voice-memo-analysis'}
              panelId={`voice-memo-${panelContent}-${selectedMemo?.id || 'list'}`}
              defaultSize={PANEL_SIZES.VOICE_MEMO_DEFAULT}
              minSize={PANEL_SIZES.VOICE_MEMO_MIN}
              className="border-l border-primary/20"
            >
              {/* Show document list if multiple documents, otherwise show single document */}
              {previewDocuments.length > 1 ? (
                <VoiceMemoDocumentList
                  documents={previewDocuments}
                  selectedDocumentId={selectedDocumentId}
                  onSelectDocument={handleSelectDocument}
                  type={panelContent === 'transcript' ? 'transcript' : 'analysis'}
                />
              ) : (
                <>
                  {panelContent === 'transcript' && selectedMemo && (
                    <VoiceMemoTranscriptPanel
                      memo={selectedMemo}
                      onClose={handleClosePanel}
                      onGenerateTitle={handleGenerateTitle}
                      onUpdateTitle={handleUpdateTitle}
                    />
                  )}
                  {panelContent === 'analysis' && selectedMemo && (
                    <VoiceMemoAnalysisPanel
                      memo={selectedMemo}
                      onClose={handleClosePanel}
                      onGenerateTitle={handleGenerateTitle}
                      onUpdateTitle={handleUpdateTitle}
                    />
                  )}
                </>
              )}
            </SecondaryPanel>
          </>
        )}
      </PanelGroup>
    </div>
  );
}
